import { createPortal } from 'react-dom';
import { useSlotCanvasStore } from '@/store/slotCanvasStore';
import { useRegisterSlotCanvas } from '../../slot-canvas/useRegisterSlotCanvas';
import { SlotCanvasLayout } from '../../slot-canvas/SlotCanvasLayout';
import { TaskDetailBody } from '@/components/tasks/detail/TaskDetailBody';
import { TaskDetailFooter } from '@/components/tasks/detail/TaskDetailFooter';
import { useOpenTaskInChat } from '@/components/tasks/useOpenTaskInChat';
import { ArtifactCanvasSlot, artifactSlotId } from '@/components/artifact-canvas';
import { useTaskArtifacts } from '@/hooks/useArtifacts';

/** The canvas is one slot per task, so two cards for the same task share one. */
export const taskSlotId = (taskId: string) => `task_detail-${taskId}`;

/**
 * The slot of the task a thread was opened from. One per thread and distinct
 * from the cards' ids, so a card the agent later emits for the same task keeps
 * its own receipt-style slot rather than re-registering (and, on unmount,
 * unregistering) the thread's panel.
 */
export const TASK_THREAD_SLOT_ID = 'task-thread-canvas';

export interface TaskDetailSlotProps {
  taskId: string;
  /** The card's title, which names the slot before the task itself is fetched. */
  title: string;
  /**
   * Where the slot comes from. `card` (the default) is a receipt in the
   * transcript: registered, never opened on its own. `thread` is the task the
   * conversation was opened from ("Open in chat" on the task panel): open from
   * first render and collapse-only, since there is no chip to reopen it from.
   */
  host?: 'card' | 'thread';
}

/**
 * Registers one task's details as a canvas slot, opened by clicking its card.
 *
 * Registration is not opening: cards are receipts that stay in the transcript,
 * so a conversation reloaded with five of them registers five slots and opens
 * none of them. The exception is the task a thread was opened from
 * (`host="thread"`), which is what the thread is about and so is open on
 * arrival — the same host-driven treatment the routine preview gets.
 *
 * The task's attachments get canvas slots of their own, mounted alongside so
 * one can be opened over the task and the back arrow returns here.
 */
export const TaskDetailSlot = ({ taskId, title, host = 'card' }: TaskDetailSlotProps) => {
  const bodyMount = useSlotCanvasStore((state) => state.bodyMountElement);
  const openSlot = useSlotCanvasStore((state) => state.openSlot);
  const openInChat = useOpenTaskInChat();

  const isThread = host === 'thread';
  const slotId = isThread ? TASK_THREAD_SLOT_ID : taskSlotId(taskId);

  const { isOpen } = useRegisterSlotCanvas({
    id: slotId,
    experience: 'task_detail',
    title,
    autoOpen: isThread,
    // A thread's panel must show however the thread was entered, history
    // replay included.
    ignoreSuppressAutoOpen: isThread,
    hasInStreamCard: !isThread,
  });

  /*
   * Read while the panel is open, so a transcript full of task cards fetches
   * for none of them up front, and opening the panel again re-reads: the URLs
   * an attachment carries are signed for the hour, and the chat may have
   * attached something since. What was read stays cached while an artifact of
   * its own is open, which is what keeps the slots below mounted.
   */
  const { data: attachments } = useTaskArtifacts(taskId, { enabled: isOpen });
  const attached = [ ...attachments?.notes ?? [], ...attachments?.outputs ?? [] ];
  const openedIds = new Set(attached.map((artifact) => artifact.id));

  /*
   * The artifact slots live outside the portal branch below. While one of them
   * is the open slot this task's `isOpen` is false, and a slot unmounted at
   * that moment would unregister the very slot the canvas is showing and close
   * it.
   */
  return (
    <>
      {[ ...openedIds ].map((artifactId) => (
        <ArtifactCanvasSlot
          key={artifactId}
          artifactId={artifactId}
          title={attached.find((artifact) => artifact.id === artifactId)?.title ?? 'Artifact'}
          autoOpen={false}
        />
      ))}

      {isOpen && bodyMount
        ? createPortal(
          <SlotCanvasLayout
            // The panel's name, as the docked task panel has it: the task's
            // own title is the head's first line, so the header does not
            // repeat it. The registry keeps the title, which is what the
            // navigator and an artifact's breadcrumb name this slot by.
            title="Task details"
            // The panel owns its own scroll and pins its own footer, so the
            // canvas body must not scroll it a second time or pad it a second
            // time.
            bodyClassName="flex flex-col overflow-hidden"
          >
            <TaskDetailBody
              taskId={taskId}
              onOpenArtifact={(artifact) =>
                openSlot(artifactSlotId(artifact.id), { returnTo: slotId })}
              footer={(task, save) => (
                <TaskDetailFooter
                  task={task}
                  ctaLabel="Open in new chat"
                  onMarkDone={() => save({ status: 'COMPLETED' })}
                  onOpenInChat={() => openInChat(task, 'new_chat')}
                />
              )}
            />
          </SlotCanvasLayout>,
          bodyMount,
        )
        : null}
    </>
  );
};
